import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, Save, Plus, Trash2, Loader2, Gift, Camera, 
  Percent, Ticket, Sparkles, DollarSign, Users, BarChart3,
  Settings, CreditCard, Tag, ChevronRight, Store, Image as ImageIcon, X, Coins, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { BravxBenefitsAdmin } from '../../components/admin/BravxBenefitsAdmin';
import { BravxContentAdmin } from '../../components/admin/BravxContentAdmin';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { subscriptionApi, statsApi } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const iconOptions = [
  { value: 'sparkles', label: 'Sparkles', icon: Sparkles },
  { value: 'camera', label: 'Cámara', icon: Camera },
  { value: 'percent', label: 'Descuento', icon: Percent },
  { value: 'ticket', label: 'Ticket', icon: Ticket },
  { value: 'gift', label: 'Regalo', icon: Gift },
];

const TABS = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'pagos', label: 'Pagos', icon: CreditCard },
  { id: 'beneficios', label: 'Beneficios', icon: Gift },
  { id: 'puntos', label: 'Canje por Puntos', icon: Coins },
  { id: 'contenido', label: 'Contenido Exclusivo', icon: Lock },
  { id: 'cupones', label: 'Cupones', icon: Tag },
];

const BENEFIT_CATEGORIES = [
  { value: 'gastronomia', label: 'Gastronomía' },
  { value: 'entretenimiento', label: 'Entretenimiento' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'retail', label: 'Retail' },
  { value: 'salud', label: 'Salud y Bienestar' },
  { value: 'educacion', label: 'Educación' },
  { value: 'general', label: 'General' },
];

const SubscriptionsAdminPage = () => {
  const { hasPermission } = useAuth();
  const [settings, setSettings] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [partnerBenefits, setPartnerBenefits] = useState([]);
  const [subscriberStats, setSubscriberStats] = useState({ active: 0, paying: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [savingBenefit, setSavingBenefit] = useState(false);
  
  const [newBenefitForm, setNewBenefitForm] = useState({
    title: '',
    description: '',
    category: 'general',
    terms: '',
    image_url: '',
    max_uses_per_user: 1,
    limited_coupons: 0
  });

  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_percent: 10,
    max_uses: null,
    subscribers_only: true,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, couponsRes, statsRes, benefitsRes] = await Promise.all([
        subscriptionApi.getSettings(),
        subscriptionApi.getCoupons().catch(() => ({ data: [] })),
        statsApi.get().catch(() => ({ data: { active_subscribers: 0, paying_subscribers: 0, total_public_users: 0 } })),
        api.get('/partners/admin/benefits/approved').catch(() => ({ data: [] }))
      ]);
      setSettings(settingsRes.data);
      setCoupons(couponsRes.data || []);
      setSubscriberStats({
        active: statsRes.data?.active_subscribers || 0,
        paying: statsRes.data?.paying_subscribers || 0,
        total: statsRes.data?.total_public_users || 0
      });
      setPartnerBenefits(benefitsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await subscriptionApi.updateSettings(settings);
      toast.success('Configuración guardada');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!newCoupon.code) {
      toast.error('Ingresa un código');
      return;
    }
    try {
      const response = await subscriptionApi.createCoupon(newCoupon);
      setCoupons([...coupons, response.data]);
      setNewCoupon({
        code: '',
        discount_percent: 10,
        max_uses: null,
        subscribers_only: true,
        is_active: true
      });
      toast.success('Cupón creado');
    } catch (error) {
      console.error('Error creating coupon:', error);
      toast.error('Error al crear cupón');
    }
  };

  const handleCreateManualBenefit = async () => {
    if (!newBenefitForm.title || !newBenefitForm.description) {
      toast.error('Completa título y descripción');
      return;
    }
    
    setSavingBenefit(true);
    try {
      await api.post('/partners/admin/benefits/manual', newBenefitForm);
      toast.success('Beneficio creado exitosamente');
      setShowBenefitModal(false);
      setNewBenefitForm({
        title: '',
        description: '',
        category: 'general',
        terms: '',
        image_url: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating benefit:', error);
      toast.error(error.response?.data?.detail || 'Error al crear beneficio');
    } finally {
      setSavingBenefit(false);
    }
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...settings.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    setSettings({ ...settings, benefits: newBenefits });
  };

  const addBenefit = () => {
    const newBenefit = {
      id: `benefit_${Date.now()}`,
      title: 'Nuevo beneficio',
      description: 'Descripción del beneficio',
      icon: 'gift',
      is_active: true
    };
    setSettings({ ...settings, benefits: [...settings.benefits, newBenefit] });
  };

  const removeBenefit = (index) => {
    const newBenefits = settings.benefits.filter((_, i) => i !== index);
    setSettings({ ...settings, benefits: newBenefits });
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 lg:p-6 space-y-6"
        >
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-black uppercase tracking-tight flex items-center gap-3">
                <Crown className="text-[#D4AF37]" size={28} />
                FULL BRAVX
              </h1>
              <p className="text-[#71717A] text-sm mt-1">Gestiona la suscripción premium</p>
            </div>
            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save className="mr-2" size={16} />}
              Guardar cambios
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center">
                  <DollarSign className="text-[#D4AF37]" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#71717A]">Precio mensual</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">${settings?.price_ars?.toLocaleString('es-AR')}</p>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Users className="text-green-400" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#71717A]">Suscriptores</p>
                  <p className="text-2xl font-bold text-green-400">{subscriberStats.active}</p>
                  <p className="text-[10px] text-[#52525B]">de {subscriberStats.total} usuarios</p>
                </div>
              </div>
            </div>
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <BarChart3 className="text-blue-400" size={24} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#71717A]">Ingresos est.</p>
                  <p className="text-2xl font-bold text-blue-400">
                    ${((subscriberStats.paying || 0) * (settings?.price_ars || 0)).toLocaleString('es-AR')}
                  </p>
                  <p className="text-[10px] text-[#52525B]">mensual ({subscriberStats.paying} pagos)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-[#121212] border border-[#27272A] rounded-xl overflow-hidden">
            {/* Tab Header */}
            <div className="flex border-b border-[#27272A] overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-wider whitespace-nowrap transition-all relative ${
                    activeTab === tab.id
                      ? 'text-[#D4AF37] bg-[#D4AF37]/5'
                      : 'text-[#71717A] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <tab.icon size={18} />
                  {tab.label}
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="activeTabIndicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#D4AF37]"
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              <AnimatePresence mode="wait">
                {/* GENERAL TAB */}
                {activeTab === 'general' && (
                  <motion.div
                    key="general"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-[#A1A1AA]">Nombre del plan</Label>
                        <Input
                          value={settings?.name || ''}
                          onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                          className="h-12 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#D4AF37]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-[#A1A1AA]">Precio mensual (ARS)</Label>
                        <Input
                          type="number"
                          value={settings?.price_ars || 0}
                          onChange={(e) => setSettings({ ...settings, price_ars: parseFloat(e.target.value) })}
                          className="h-12 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-[#A1A1AA]">Descripción</Label>
                      <Input
                        value={settings?.description || ''}
                        onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                        className="h-12 bg-[#0A0A0A] border-[#27272A] text-white focus:border-[#D4AF37]"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-[#A1A1AA]">Días de prueba gratis</Label>
                        <Input
                          type="number"
                          min="0"
                          max="90"
                          value={settings?.trial_days ?? 15}
                          onChange={(e) => setSettings({ ...settings, trial_days: Math.max(0, parseInt(e.target.value) || 0) })}
                          className="h-12 bg-[#0A0A0A] border-[#27272A] text-white w-32 focus:border-[#D4AF37]"
                        />
                        <p className="text-xs text-[#52525B]">0 = desactivado. Máx: 90 días.</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-[#A1A1AA]">Fotos HD gratis por mes</Label>
                        <Input
                          type="number"
                          value={settings?.free_hd_photos_per_month || 5}
                          onChange={(e) => setSettings({ ...settings, free_hd_photos_per_month: parseInt(e.target.value) })}
                          className="h-12 bg-[#0A0A0A] border-[#27272A] text-white w-32 focus:border-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-xl border border-[#27272A]">
                      <Switch
                        checked={settings?.is_active}
                        onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
                      />
                      <div>
                        <Label className="text-white font-medium">Suscripciones activas</Label>
                        <p className="text-[#71717A] text-xs">Habilita o deshabilita nuevas suscripciones</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* PAGOS TAB */}
                {activeTab === 'pagos' && (
                  <motion.div
                    key="pagos"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    <div className="bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/30 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                          <CreditCard className="text-[#D4AF37]" size={24} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-white text-lg mb-1">Integración Rebill</h3>
                          <p className="text-[#A1A1AA] text-sm mb-4">Pagos recurrentes con tarjeta de crédito/débito</p>
                          
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-[#A1A1AA]">Link de Suscripción Rebill</Label>
                              <Input
                                value={settings?.rebill_subscription_link || ''}
                                onChange={(e) => setSettings({ ...settings, rebill_subscription_link: e.target.value })}
                                placeholder="https://pay.rebill.com/tu-organizacion/..."
                                className="h-12 bg-[#0A0A0A] border-[#27272A] text-white font-mono text-sm focus:border-[#D4AF37]"
                              />
                            </div>

                            <div className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-xl border border-[#27272A]">
                              <Switch
                                checked={settings?.rebill_enabled !== false}
                                onCheckedChange={(checked) => setSettings({ ...settings, rebill_enabled: checked })}
                              />
                              <div>
                                <Label className="text-white font-medium">Habilitar Rebill</Label>
                                <p className="text-[#71717A] text-xs">Pagos con tarjeta directa</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Sparkles className="text-yellow-500 flex-shrink-0 mt-0.5" size={18} />
                        <div className="space-y-2 text-sm">
                          <p className="text-yellow-500 font-medium">Integración Rebill Activa (Producción)</p>
                          <ul className="text-yellow-500/80 space-y-1 text-xs">
                            <li>• Clave Pública: pk_9214...b81a84</li>
                            <li>• Webhook URL: <code className="bg-black/30 px-1 rounded">{process.env.REACT_APP_BACKEND_URL}/api/subscriptions/rebill/webhook</code></li>
                            <li>• URL de retorno: <code className="bg-black/30 px-1 rounded">{process.env.REACT_APP_BACKEND_URL}/mi-cuenta?payment=success</code></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* BENEFICIOS TAB */}
                {activeTab === 'beneficios' && (
                  <motion.div
                    key="beneficios"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    {/* Plan Benefits */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-white">Beneficios del Plan</h3>
                          <p className="text-[#71717A] text-sm">Aparecen en la página de suscripción</p>
                        </div>
                        <Button onClick={addBenefit} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                          <Plus className="mr-2" size={16} />
                          Agregar
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {settings?.benefits?.map((benefit, index) => (
                          <motion.div
                            key={benefit.id}
                            className="bg-[#0A0A0A] border border-[#27272A] p-4 rounded-xl"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                              <div className="md:col-span-2">
                                <Label className="text-xs text-[#71717A] mb-1 block">Ícono</Label>
                                <select
                                  value={benefit.icon}
                                  onChange={(e) => updateBenefit(index, 'icon', e.target.value)}
                                  className="w-full bg-[#121212] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm"
                                >
                                  {iconOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-3">
                                <Label className="text-xs text-[#71717A] mb-1 block">Título</Label>
                                <Input
                                  value={benefit.title}
                                  onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                                  className="h-10 bg-[#121212] border-[#27272A] text-white"
                                />
                              </div>
                              <div className="md:col-span-6">
                                <Label className="text-xs text-[#71717A] mb-1 block">Descripción</Label>
                                <Input
                                  value={benefit.description}
                                  onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                                  className="h-10 bg-[#121212] border-[#27272A] text-white"
                                />
                              </div>
                              <div className="md:col-span-1 flex justify-end">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeBenefit(index)}
                                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </div>
                          </motion.div>
                        ))}

                        {(!settings?.benefits || settings.benefits.length === 0) && (
                          <div className="text-center py-8 text-[#52525B]">
                            <Gift size={40} className="mx-auto mb-3 opacity-50" />
                            <p>No hay beneficios configurados</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-[#27272A] pt-6">
                      {/* Manual Benefits Section */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-white flex items-center gap-2">
                              <Store size={18} className="text-[#D4AF37]" />
                              Beneficios Manuales
                            </h3>
                            <p className="text-[#71717A] text-sm">Crea beneficios directamente para FULL BRAVX</p>
                          </div>
                          <Button 
                            onClick={() => setShowBenefitModal(true)} 
                            className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold"
                          >
                            <Plus className="mr-2" size={16} />
                            Crear Beneficio
                          </Button>
                        </div>

                        {/* Existing Partner Benefits */}
                        {partnerBenefits.length > 0 && (
                          <div className="grid gap-3">
                            {partnerBenefits.slice(0, 5).map((benefit, index) => (
                              <div 
                                key={benefit.id}
                                className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 flex items-center gap-4"
                              >
                                {benefit.image_url ? (
                                  <img src={benefit.image_url} alt="" className="w-16 h-16 rounded-lg object-cover bg-[#121212]" />
                                ) : (
                                  <div className="w-16 h-16 rounded-lg bg-[#121212] flex items-center justify-center">
                                    <Gift className="text-[#3F3F46]" size={24} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-white truncate">{benefit.title}</h4>
                                  <p className="text-[#71717A] text-sm truncate">{benefit.description}</p>
                                  <span className="text-xs text-[#52525B]">{benefit.partner_name || 'Bravo'}</span>
                                </div>
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Activo</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Link to Partner Benefits Approval */}
                    <div className="p-4 bg-[#18181B] border border-[#27272A] rounded-xl">
                      <Link 
                        to="/admin/aprobar-beneficios" 
                        className="flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                            <Gift className="text-[#D4AF37]" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold text-white group-hover:text-[#D4AF37] transition-colors">Aprobar Beneficios de Partners</p>
                            <p className="text-[#71717A] text-xs">Revisar beneficios pendientes</p>
                          </div>
                        </div>
                        <ChevronRight className="text-[#52525B] group-hover:text-[#D4AF37] transition-colors" size={20} />
                      </Link>
                    </div>
                  </motion.div>
                )}

                {/* CANJE POR PUNTOS TAB */}
                {activeTab === 'puntos' && (
                  <motion.div key="puntos" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <BravxBenefitsAdmin />
                  </motion.div>
                )}

                {/* CONTENIDO EXCLUSIVO TAB */}
                {activeTab === 'contenido' && (
                  <motion.div key="contenido" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <BravxContentAdmin />
                  </motion.div>
                )}

                {/* CUPONES TAB */}
                {activeTab === 'cupones' && (
                  <motion.div
                    key="cupones"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-6"
                  >
                    {/* New Coupon Form */}
                    <div className="bg-[#0A0A0A] border border-[#27272A] p-5 rounded-xl">
                      <h4 className="font-bold text-white mb-4">Crear nuevo cupón</h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="space-y-2">
                          <Label className="text-xs text-[#71717A]">Código</Label>
                          <Input
                            value={newCoupon.code}
                            onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                            placeholder="BRAVX20"
                            className="h-11 bg-[#121212] border-[#27272A] text-white font-mono"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-[#71717A]">Descuento %</Label>
                          <Input
                            type="number"
                            value={newCoupon.discount_percent}
                            onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: parseFloat(e.target.value) })}
                            className="h-11 bg-[#121212] border-[#27272A] text-white"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-[#71717A]">Usos máximos</Label>
                          <Input
                            type="number"
                            value={newCoupon.max_uses || ''}
                            onChange={(e) => setNewCoupon({ ...newCoupon, max_uses: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Ilimitado"
                            className="h-11 bg-[#121212] border-[#27272A] text-white"
                          />
                        </div>
                        <Button 
                          onClick={handleCreateCoupon} 
                          className="h-11 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold"
                        >
                          <Plus className="mr-2" size={16} />
                          Crear
                        </Button>
                      </div>
                    </div>

                    {/* Existing Coupons */}
                    <div className="space-y-2">
                      <h4 className="font-bold text-white">Cupones existentes</h4>
                      {coupons.length > 0 ? (
                        <div className="space-y-2">
                          {coupons.map((coupon, index) => (
                            <motion.div
                              key={coupon.id || index}
                              className="flex items-center justify-between bg-[#0A0A0A] border border-[#27272A] p-4 rounded-xl"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              <div className="flex items-center gap-4">
                                <code className="bg-[#D4AF37]/20 text-[#D4AF37] px-4 py-2 rounded-lg font-mono font-bold">
                                  {coupon.code}
                                </code>
                                <span className="text-white">{coupon.discount_percent}% descuento</span>
                                {coupon.max_uses && (
                                  <span className="text-[#52525B] text-sm">
                                    {coupon.times_used || 0}/{coupon.max_uses} usos
                                  </span>
                                )}
                              </div>
                              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                coupon.is_active 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : 'bg-red-500/20 text-red-400'
                              }`}>
                                {coupon.is_active ? 'Activo' : 'Inactivo'}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-[#52525B]">
                          <Tag size={40} className="mx-auto mb-3 opacity-50" />
                          <p>No hay cupones creados</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Create Manual Benefit Modal */}
          <Dialog open={showBenefitModal} onOpenChange={setShowBenefitModal}>
            <DialogContent className="bg-[#121212] border border-[#27272A] max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#F59E0B] rounded-lg flex items-center justify-center">
                    <Gift size={20} className="text-black" />
                  </div>
                  Crear Beneficio Manual
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <div>
                  <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Título del Beneficio *</Label>
                  <Input
                    value={newBenefitForm.title}
                    onChange={(e) => setNewBenefitForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Ej: 2x1 en entradas VIP"
                    className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Descripción *</Label>
                  <textarea
                    value={newBenefitForm.description}
                    onChange={(e) => setNewBenefitForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe el beneficio..."
                    rows={3}
                    className="w-full px-4 py-3 bg-[#18181B] border border-[#27272A] rounded-xl text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Categoría</Label>
                    <select
                      value={newBenefitForm.category}
                      onChange={(e) => setNewBenefitForm(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full h-12 px-4 bg-[#18181B] border border-[#27272A] rounded-xl text-white focus:border-[#D4AF37] focus:outline-none"
                    >
                      {BENEFIT_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Imagen URL</Label>
                    <Input
                      value={newBenefitForm.image_url}
                      onChange={(e) => setNewBenefitForm(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://..."
                      className="h-12 bg-[#18181B] border-[#27272A] text-white focus:border-[#D4AF37]"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Términos y Condiciones</Label>
                  <textarea
                    value={newBenefitForm.terms}
                    onChange={(e) => setNewBenefitForm(prev => ({ ...prev, terms: e.target.value }))}
                    placeholder="Condiciones del beneficio (opcional)..."
                    rows={2}
                    className="w-full px-4 py-3 bg-[#18181B] border border-[#27272A] rounded-xl text-white placeholder:text-[#52525B] focus:border-[#D4AF37] focus:outline-none resize-none"
                  />
                </div>

                {/* Limited Coupons */}
                <div>
                  <Label className="text-sm font-medium text-[#A1A1AA] mb-2 block">Cupones Limitados</Label>
                  <p className="text-[#52525B] text-xs mb-2">Cuando se agoten, el beneficio se desactiva</p>
                  <div className="flex flex-wrap gap-2">
                    {[0, 10, 20, 50, 100].map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setNewBenefitForm(prev => ({ ...prev, limited_coupons: num }))}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                          newBenefitForm.limited_coupons === num
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-[#27272A] bg-[#0A0A0A] text-white hover:border-[#3F3F46]'
                        }`}
                      >
                        {num === 0 ? 'Ilimitados' : `${num} cupones`}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-[#27272A]">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowBenefitModal(false)}
                    className="text-[#A1A1AA] hover:text-white"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleCreateManualBenefit}
                    disabled={savingBenefit} 
                    className="bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold hover:shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                  >
                    {savingBenefit ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                    Crear Beneficio
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </motion.div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default SubscriptionsAdminPage;
